import { Injectable } from '@angular/core';
import {
  RESEND_PIN_SUBJECT,
  Agb,
  ResendPinResponse,
  User,
  UserMetadata,
} from '../../models';
import { ServerCallerService } from '../server-caller/server-caller.service';
import { PrivateServerCallerService } from '../server-caller/private-server-caller.service';
import { PaymentMethod } from '../../models/interfaces/PaymentMethod.interface';
import { PaymentMethodSetup } from '../../models/interfaces/PaymentMethodSetup.interface';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private serverCaller: ServerCallerService,
    private privateServerCallerService: PrivateServerCallerService
  ) {}

  // region User

  /**
   * Get User Metadata
   * @param {String}  username :  phone number or email of user
   * */
  public async getUserMetadata(username: string): Promise<UserMetadata> {
    const url = `/v1/user-metadata/${username}`;
    const response = await this.serverCaller.get(url);
    return response?.body;
  }

  public async getCurrentLoggedInUser(): Promise<User> {
    const url = `/v1/user/me`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async createNewUser(user: User): Promise<User> {
    const url = '/v1/user';

    const response = await this.serverCaller.post(url, user);

    return response?.body;
  }

  public async createNewUserFromCustomer(
    emailOrPhoneNr: string
  ): Promise<boolean> {
    const url = '/v1/customer-users';

    const response = await this.serverCaller.post(url, {
      identity: emailOrPhoneNr,
    });

    return response?.status === 201;
  }

  public async updateUser(user: User): Promise<boolean> {
    const url = `/v1/user/${user.id}`;
    const response = await this.privateServerCallerService.put(url, user);
    return response?.status === 200;
  }

  public async changePinOfUser(userId: number, pin: string): Promise<boolean> {
    const url = `/v1/user/${userId}/pin`;

    const response = await this.privateServerCallerService.put(url, pin);
    return response?.status === 200;
  }

  // return challenge ID, BE sends code to email
  public async updateEmailIfNotVerified(
    userId: number,
    email: string
  ): Promise<ResendPinResponse> {
    const url = `/v1/user/${userId}/email`;

    const response = await this.privateServerCallerService.patch(url, {
      value: email,
    });

    return response?.body;
  }

  public async updatePhoneNumber(
    userId: number,
    phoneNr: string
  ): Promise<ResendPinResponse> {
    const url = `/v1/user/${userId}/phoneNr`;

    const response = await this.privateServerCallerService.patch(url, {
      value: phoneNr,
    });

    return response?.body;
  }

  public async confirmPhoneNrOrEmail(
    challengeId: string | undefined | null,
    code: string | undefined | null
  ): Promise<string> {
    const url = `/v1/challenges/${challengeId}?code=${code}`;
    const response = await this.serverCaller.delete(url);
    return response?.body?.token;
  }

  public async getCodeToConfirmEmail(
    email: string | undefined | null
  ): Promise<ResendPinResponse> {
    const url = '/v1/codes';

    const response = await this.serverCaller.post(url, {
      identity: email,
      subject: RESEND_PIN_SUBJECT.CONFIRM_EMAIL,
    });

    return response?.body;
  }

  public async getCodeToConfirmPhoneNumber(
    phoneNr: string | undefined | null
  ): Promise<ResendPinResponse> {
    const url = '/v1/codes';

    const response = await this.serverCaller.post(url, {
      identity: phoneNr,
      subject: RESEND_PIN_SUBJECT.CONFIRM_PHONE_NUMBER,
    });

    return response?.body;
  }

  // endregion

  // region Payment method

  public async getAllPaymentMethodsOfLoggedInUser(): Promise<PaymentMethod[]> {
    const url = `/v1/payment-methods`;
    const response = await this.privateServerCallerService.get(url);

    const paymentMethods = response?.body;
    paymentMethods.sort((a: PaymentMethod, b: PaymentMethod) => {
      if (a.isDefault) {
        return -1;
      }

      if (b.isDefault) {
        return 1;
      }

      return 0;
    });

    return paymentMethods;
  }

  public async createPaymentMethodSetup(): Promise<PaymentMethodSetup> {
    const url = `/v1/payment-method-setups`;
    const response = await this.privateServerCallerService.post(url, {
      provider: 'STRIPE',
    });

    return response?.body;
  }

  public async finishPaymentMethodSetup(
    paymentMethodSetupId: string
  ): Promise<boolean> {
    const url = `/v1/payment-method-setups/${paymentMethodSetupId}`;
    const response = await this.privateServerCallerService.patch(url, null);

    return response?.status === 200;
  }

  public async updatePaymentMethod(
    paymentMethod: PaymentMethod
  ): Promise<boolean> {
    const url = `/v1/payment-methods/${paymentMethod?.id}`;
    const response = await this.privateServerCallerService.patch(url, {
      isDefault: paymentMethod.isDefault,
    });

    return response?.status === 204;
  }

  public async deletePaymentMethod(
    paymentMethod: PaymentMethod
  ): Promise<boolean> {
    const url = `/v1/payment-methods/${paymentMethod?.id}`;
    const response = await this.privateServerCallerService.delete(url);

    return response?.status === 204;
  }

  // endregion

  // region AGB

  public async getCurrentAgb(): Promise<Agb> {
    const url = '/v1/agb?tag=CURRENT';

    const response = await this.serverCaller.get(url);

    return response?.body;
  }

  public async getAgb(locale: string): Promise<Agb> {
    const url = `/v1/agb?tag=LATEST&locale=${locale}`;
    const response = await this.serverCaller.get(url);

    return response?.body;
  }

  public async updateAgbAgreement(
    userId: number,
    version: number
  ): Promise<boolean> {
    const url = `/v1/user/${userId}/agb-agreement`;
    const response = await this.privateServerCallerService.patch(url, {
      accepted: true,
      lastSeenVersion: version,
    });
    return response?.status === 200;
  }

  // endregion
}
